import CancelSharpIcon from "@mui/icons-material/CancelSharp";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import _without from "lodash/without";
import dayjs, { Dayjs } from "dayjs"; // dayjs is used for DatePicker and age calculation
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  getAllServices,
  updateClinicianProfile,
} from "../../../api/services/api";
import ImageUpload from "../../../components/ImageUpload";

export const EditProfileForm = ({ prevData, toggleDrawer }: any) => {
  const queryClient = useQueryClient();
  const [dob, setDob] = useState<Dayjs | null>(
    prevData?.dob ? dayjs(prevData.dob) : null
  );
  const [picDetails, setPicDetails] = useState<any>(null);
  const [editImage, setEditImage] = useState(prevData?.imageUrl);
  const [selecteServices, setSelecteServices] = useState<any>(
    prevData?.services ? prevData?.services : []
  );

  const { data: serviceData } = useQuery("services", getAllServices);

  const handleChangeSymptom = (e: any) => {
    setSelecteServices(e.target.value);
  };

  const handleDelete = (e: React.MouseEvent, value: string) => {
    e.preventDefault();
    setSelecteServices((current: any) => _without(current, value));
  };

  const { mutate } = useMutation(updateClinicianProfile, {
    onSuccess: () => {
      queryClient.invalidateQueries("clinician-profile");
      toggleDrawer(false);
      toast.success("Profile Updated Successfully");
    },
    onError: (res: any) => {
      const messages = res?.response?.data?.message;
      if (Array.isArray(messages)) {
        messages.forEach((item: any) => toast.error(item));
      } else if (typeof messages === "string") {
        toast.error(messages);
      } else {
        toast.error("An unexpected error occurred while updating profile.");
      }
    },
  });

  const { register, handleSubmit, control } = useForm({
    mode: "onSubmit",
    defaultValues: {
      fullName: prevData?.user?.fullName,
      mobileNumber: prevData?.user?.mobileNumber,
      gender: prevData?.gender,
      email: prevData?.user?.email,
      bio: prevData?.bio,
      dob: prevData?.dob ? dayjs(prevData.dob) : null,
    },
  });

  const calculateAgeFromDob = (birthDate: Dayjs | null): number | null => {
    if (!birthDate || !birthDate.isValid()) return null;
    return dayjs().diff(birthDate, "year");
  };

  const onSubmit = (data: any) => {
    const calculatedAge = calculateAgeFromDob(dob);

    const submissionData: any = {
      ...data,
      key: picDetails?.key,
      dob: dob ? dob.format("YYYY-MM-DD") : null,
      serviceIds: selecteServices?.map((item: any) => item?.id),
    };

    if (calculatedAge !== null) {
      submissionData.age = calculatedAge;
    } else if (dob) {
      console.warn(
        "DOB is present but age could not be calculated. Age will not be sent."
      );
    }

    if (dob && submissionData.age === undefined && calculatedAge === null) {
      toast.error(
        "Could not calculate age from the provided Date of Birth. Please check the DOB."
      );
      return; // Prevent submission if age is expected but not calculable
    }

    const finalSubmissionData: Record<string, any> = {};
    for (const key in submissionData) {
      if (submissionData[key] !== undefined) {
        finalSubmissionData[key] = submissionData[key];
      }
    }

    if (finalSubmissionData.dob && finalSubmissionData.age === undefined) {
      if (calculatedAge !== null) {
        finalSubmissionData.age = calculatedAge;
      } else {
        console.error(
          "Critical: DOB is set but age is not. Backend will likely reject."
        );
      }
    }

    mutate({ body: finalSubmissionData, id: prevData?.id });
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box width="550px" display="flex" flexDirection="column" gap={3}>
          <ImageUpload
            picDetails={picDetails}
            setPicDetails={setPicDetails}
            editImage={editImage}
            setEditImage={setEditImage}
            title={"photo"}
          />
          <TextField
            label="Name"
            variant={"standard"}
            fullWidth={true}
            {...register("fullName", { required: "Name is required" })}
          />
          <TextField
            label="Mobile Number"
            variant={"standard"}
            disabled
            fullWidth={true}
            {...register("mobileNumber", {
              required: "Mobile number is required",
            })}
          />
          <FormControl component="fieldset" fullWidth={true}>
            <FormLabel component="legend">Gender *</FormLabel>
            <Controller
              rules={{ required: "Gender is required" }}
              control={control}
              name="gender"
              defaultValue={prevData?.gender || ""}
              render={({ field }) => (
                <RadioGroup {...field} row>
                  <FormControlLabel
                    value="MALE"
                    control={<Radio />}
                    label="Male"
                  />
                  <FormControlLabel
                    value="FEMALE"
                    control={<Radio />}
                    label="Female"
                  />
                  <FormControlLabel
                    value="OTHERS"
                    control={<Radio />}
                    label="Others"
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
          <FormControl component="fieldset" fullWidth={true}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="dob"
                control={control}
                rules={{ required: "Date of Birth is required" }}
                defaultValue={prevData?.dob ? dayjs(prevData.dob) : null}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label="Date of Birth *"
                    value={field.value}
                    maxDate={dayjs()}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                      setDob(newValue);
                    }}
                    inputFormat="DD/MM/YYYY"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant={"standard"}
                        fullWidth={true}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </LocalizationProvider>
          </FormControl>
          <FormControl fullWidth>
            <FormLabel component="legend">Services *</FormLabel>
            <Select
              fullWidth
              multiple
              required
              variant="standard"
              value={selecteServices}
              onChange={handleChangeSymptom}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value: any) => (
                    <Chip
                      key={value?.id}
                      clickable
                      deleteIcon={
                        <CancelSharpIcon
                          onMouseDown={(event: any) => event.stopPropagation()}
                        />
                      }
                      label={value?.title}
                      onDelete={(e: any) => handleDelete(e, value)}
                    />
                  ))}
                </Box>
              )}
            >
              {serviceData?.data?.services?.map((item: any, index: number) => (
                <MenuItem key={index} value={item}>
                  {item?.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Email Address"
            variant={"standard"}
            disabled
            fullWidth={true}
            {...register("email", {
              pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
              required: "Email is required",
            })}
          />
          <TextField
            label="Bio"
            variant={"standard"}
            fullWidth={true}
            multiline
            rows={2}
            {...register("bio", { required: "Bio is required" })}
          />
        </Box>
        <Button
          fullWidth={true}
          type="submit"
          variant="contained"
          sx={{ marginTop: "32px", p: 2 }}
        >
          Submit
        </Button>
      </form>
    </Box>
  );
};
